// CareRxLabTracker — D-CRX-44 (Patrick approved 2026-07-30): the Lab Tracker owns the lab-violation
// flow. Only enrollments (therapies) with a lab violation appear. Main worklist → dial into a
// patient → set provider, work outreach, walk draw statuses, bulk-edit, re-draw (lock + new order).
// Collected across the board → the enrollment advances to Clinical Review and leaves the tracker.
import { useMemo, useState, useCallback } from 'react'
import * as api from '../../api/client'
import SearchableSelect from '../../components/common/SearchableSelect'
import LabPanel from '../../components/carerx/LabPanel'
import { ColumnFilterButton, ColumnFilterChips, applyColumnFilters, filterToken, type ColumnFilters } from '../../components/common/ColumnFilter'
import PatientLanguageBadge from '../../components/ui/PatientLanguageBadge'
import LabNextActionCell from './LabNextActionCell'
import { rowActions } from './rowOpen'
import DateInput from '../../components/ui/DateInput'
import TruncatedText from '../../components/ui/TruncatedText'
import TruncationNotice from '../../components/ui/TruncationNotice'
import { useLabProviders, useLabTracker, useLabTrackerDetail, useLabTrackerWrites } from '../../hooks/queries/carerxLabTracker'
import { friendlyError } from '../../api/errors'
import { formatUsDateTime } from '../../api/format'
import { StatTile } from '../../components/ui/Section'
import { LAB_TILES, labTileRows, nextLabTile, type LabTileKey } from './labTrackerTiles'

const T = {
  text: 'var(--color-text, #1a1a1a)', soft: 'var(--color-text-soft, #6b7280)', mute: 'var(--color-text-mute, #9ca3af)',
  border: 'var(--color-border, #e5e7eb)', surface: 'var(--color-surface, #fff)', s2: 'var(--color-surface-2, #f9fafb)',
  primary: 'var(--color-primary, #185FA5)', pb: 'var(--color-primary-bg, #eef5fc)',
  ok: '#085041', okbg: '#E1F5EE', warn: '#92400e', warnbg: '#fef3c7', bad: '#b91c1c', badbg: '#fef2f2',
}
const card: React.CSSProperties = { background: T.surface, border: `0.5px solid ${T.border}`, borderRadius: 12, padding: '14px 16px', marginBottom: 14 }
const th: React.CSSProperties = { textAlign: 'left', padding: '8px 9px', fontSize: 11.5, color: T.soft, borderBottom: `1px solid ${T.border}`, whiteSpace: 'nowrap', fontWeight: 600 }
const td: React.CSSProperties = { padding: '8px 9px', borderBottom: `0.5px solid ${T.border}`, fontSize: 12.5, verticalAlign: 'middle' }
const btn: React.CSSProperties = { fontSize: 12, padding: '6px 12px', border: `1px solid ${T.border}`, borderRadius: 8, background: 'transparent', cursor: 'pointer', color: T.text }
const btnP: React.CSSProperties = { ...btn, background: T.primary, color: '#fff', border: 'none', fontWeight: 600 }
const inp: React.CSSProperties = { fontSize: 12.5, padding: '5px 7px', border: `1px solid ${T.border}`, borderRadius: 6, background: T.surface }
const pill = (bg: string, fg: string): React.CSSProperties => ({ fontSize: 10.5, padding: '2px 9px', borderRadius: 20, background: bg, color: fg, whiteSpace: 'nowrap', display: 'inline-block' })
const stColor = (s: string) => s === 'collected' || s === 'closed' ? pill(T.okbg, T.ok) : s === 'in_progress' ? pill(T.pb, T.primary) : s === 'redraw_needed' ? pill(T.badbg, T.bad) : pill(T.warnbg, T.warn)
const usDate = (v?: string | null) => { if (!v) return '—'; const d = new Date(v); return isNaN(d.getTime()) ? '—' : `${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}-${d.getFullYear()}` }
const DRAW_STATUS = ['pending', 'scheduled', 'collected', 'redraw_needed']
const dateStr = (v?: string | null) => (v ? String(v).slice(0, 10) : '')

type SortDir = 'asc' | 'desc' | null
// D-UI-2: every column filters. `shown` is what the cell prints when that differs from the sort
// value in `get`; `filter: 'date'` draws From/To instead of a value list.
interface ColDef { key: string; label: string; get: (r: api.LabTrackerRow) => string | number; shown?: (r: api.LabTrackerRow) => string; filter?: 'text' | 'select' | 'date'; opts?: string[] }
const COLS: ColDef[] = [
  { key: 'rx_number', label: 'Rx', get: r => r.rx_number ?? '', filter: 'text' },
  { key: 'patient_name', label: 'Patient', get: r => r.patient_name, filter: 'text' },
  { key: 'drug_name', label: 'Drug', get: r => r.drug_name ?? '', filter: 'text' },
  { key: 'open_count', label: '# Labs', get: r => r.open_count, shown: r => `${r.open_count} open / ${r.lab_count}`, filter: 'select' },
  { key: 'status', label: 'Status', get: r => r.status, filter: 'select', opts: ['pending', 'in_progress', 'collected', 'closed'] },
  { key: 'provider_name', label: 'Lab provider', get: r => r.provider_name ?? '', filter: 'select' },
  { key: 'patient_outreach_status', label: 'Patient outreach', get: r => r.patient_outreach_status ?? '', filter: 'select', opts: ['not_contacted', 'contacted', 'unreachable'] },
  { key: 'patient_outreach_on', label: 'Pt. date', get: r => r.patient_outreach_on ?? '', shown: r => dateStr(r.patient_outreach_on), filter: 'date' },
  { key: 'provider_outreach_status', label: 'Provider outreach', get: r => r.provider_outreach_status ?? '', filter: 'select', opts: ['not_contacted', 'contacted', 'unreachable'] },
  { key: 'provider_outreach_on', label: 'Prov. date', get: r => r.provider_outreach_on ?? '', shown: r => dateStr(r.provider_outreach_on), filter: 'date' },
  { key: 'draw_types', label: 'Draw type', get: r => r.draw_types ?? '', filter: 'text' },
  // ASP #28: what staff typed wins in the filter/sort; the derived action stands in when nothing is typed.
  { key: 'next_action', label: 'Next action', get: r => r.next_action_text || r.next_action, filter: 'text' },
  { key: 'notes', label: 'Notes', get: r => [r.clinical_notes, r.last_review_note].filter(Boolean).join(' · '), filter: 'text' },
  { key: 'follow_up', label: 'Follow up', get: r => r.follow_up ?? '', shown: r => dateStr(r.follow_up), filter: 'date' },
]

export default function CareRxLabTracker() {
  const list = useLabTracker()
  const providersQ = useLabProviders()
  const w = useLabTrackerWrites()
  const rows = useMemo(() => list.data?.items ?? [], [list.data])
  const providers = useMemo(() => providersQ.data?.items ?? [], [providersQ.data])
  const truncated = !!list.data?.truncated
  const cap = list.data?.cap ?? null
  const [sel, setSel] = useState<string | null>(null)
  const [colFilters, setColFilters] = useState<ColumnFilters>({})
  const [sortKey, setSortKey] = useState<string>('')
  const [sortDir, setSortDir] = useState<SortDir>(null)
  // CareRx wave-1 #2: the dashboard tile whose set the table shows (null = every row).
  const [tile, setTile] = useState<LabTileKey | null>(null)
  const { refetch } = list
  const load = useCallback(() => { void refetch() }, [refetch])

  if (sel) return <Detail therapyId={sel} providers={providers} onBack={() => { setSel(null); load() }} />

  // ── Dashboard rollups (computed from the loaded rows). Each tile's count is its own filter's
  // row count (labTrackerTiles.ts), so a click lists exactly what the tile counted.
  const total = rows.length
  const redrawOrders = rows.reduce((n, r) => n + (r.redraw_count || 0), 0)
  const tileRows = labTileRows(rows, tile)

  // ── Filter (shared header funnel, G-UI-1) + sort pipeline.
  const colValueOf = (r: api.LabTrackerRow, key: string) => { const c = COLS.find(x => x.key === key); return c ? (c.shown ? c.shown(r) : c.get(r)) : '' }
  const toggleSort = (k: string) => {
    if (sortKey !== k) { setSortKey(k); setSortDir('asc'); return }
    setSortDir(d => d === 'asc' ? 'desc' : d === 'desc' ? null : 'asc')
    if (sortDir === 'desc') setSortKey('')
  }
  // Distinct values for a column come from the rows filtered by the OTHER active header filters
  // (so unticking a value doesn't erase its own options — matches StationQueue).
  const distinctFor = (key: string) => {
    const others: ColumnFilters = Object.fromEntries(Object.entries(colFilters).filter(([k]) => k !== key))
    const base = applyColumnFilters(tileRows, others, colValueOf)
    return [...new Set(base.map(r => filterToken(colValueOf(r, key))))].sort((a, b) => a.localeCompare(b))
  }
  let viewRows = applyColumnFilters(tileRows, colFilters, colValueOf)
  if (sortKey && sortDir) {
    const col = COLS.find(c => c.key === sortKey)!
    viewRows = [...viewRows].sort((a, b) => {
      const av = col.get(a), bv = col.get(b)
      const cmp = typeof av === 'number' && typeof bv === 'number' ? av - bv : String(av).localeCompare(String(bv))
      return sortDir === 'asc' ? cmp : -cmp
    })
  }
  const colLabels = Object.fromEntries(COLS.map(c => [c.key, c.label]))

  return (
    <div>
      {/* Dashboard */}
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 12 }}>
        {LAB_TILES.map(t => {
          const n = t.key === 'all' ? total : rows.filter(t.match).length
          const tone = n ? t.tone : undefined
          return <StatTile key={t.key} label={t.label} value={list.isSuccess ? n : '—'}
            color={tone === 'bad' ? T.bad : tone === 'warn' ? T.warn : tone === 'ok' ? T.ok : T.text}
            hint={t.key === 'redraws' && redrawOrders !== n ? `${redrawOrders} re-draw order${redrawOrders === 1 ? '' : 's'}` : undefined}
            active={t.key === 'all' ? tile === null : tile === t.key}
            // A tile shows exactly its set: the header funnels it would otherwise intersect are cleared.
            onClick={() => { setColFilters({}); setTile(cur => nextLabTile(cur, t.key)) }} />
        })}
      </div>

      {list.isError && (
        <div role="alert" style={{ fontSize: 13, color: '#b91c1c', marginBottom: 12 }}>
          The lab tracker could not be loaded. {friendlyError(list.error)}
        </div>
      )}
      {truncated && <TruncationNotice shown={cap ?? rows.length} what="therapies in the tracker" detail="full pagination is coming" />}

      <div style={card}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 8 }}>
          <div>
            <div style={{ fontSize: 15, fontWeight: 700 }}>Lab Tracker</div>
            <div style={{ fontSize: 12, color: T.soft }}>Enrollments with a lab violation. A row closes when every required lab is collected — moving the enrollment to <b>Clinical Review</b>. Click a row to work the patient.</div>
          </div>
          {sortKey && <button style={btn} onClick={() => { setSortKey(''); setSortDir(null) }}>Clear sort</button>}
        </div>
        <ColumnFilterChips filters={colFilters} labels={colLabels}
          onClear={key => setColFilters(f => { const n = { ...f }; delete n[key]; return n })}
          onClearAll={() => setColFilters({})} />
        {/* Scrollable body with a STICKY header row (headers stay on top). The filter funnel lives
            INSIDE each header cell — the shared G-UI-1 pattern used across the system. */}
        <div style={{ overflow: 'auto', maxHeight: '62vh', border: `0.5px solid ${T.border}`, borderRadius: 8 }}>
          <table data-funnels="own" style={{ borderCollapse: 'separate', borderSpacing: 0, width: '100%', minWidth: 1300 }}>
            <thead style={{ position: 'sticky', top: 0, zIndex: 3 }}>
              <tr>
                {COLS.map(c => (
                  <th key={c.key} style={{ ...th, position: 'sticky', top: 0, background: T.s2, cursor: 'pointer', userSelect: 'none' }}
                    onClick={() => toggleSort(c.key)} title="Click to sort" role="columnheader"
                    aria-sort={sortKey === c.key ? (sortDir === 'asc' ? 'ascending' : sortDir === 'desc' ? 'descending' : 'none') : 'none'}>
                    {c.label}{sortKey === c.key ? (sortDir === 'asc' ? ' ▲' : sortDir === 'desc' ? ' ▼' : '') : ''}{c.filter ? ' ' : ''}
                    {c.filter && <ColumnFilterButton label={c.label} getValues={() => distinctFor(c.key)} isDate={c.filter === 'date'}
                      selected={colFilters[c.key] ?? new Set()}
                      onChange={next => setColFilters(f => ({ ...f, [c.key]: next }))} />}
                  </th>))}
              </tr>
            </thead>
            <tbody>
              {/* P0 B8: the all-clear is a clinical statement — only a SUCCESSFUL load may make it. While loading
                  (or after a failed load, reported above) the table says so instead of "no violations". */}
              {viewRows.length === 0 && <tr><td style={td} colSpan={14}>{list.isPending ? 'Loading the lab tracker…'
                : list.isError ? 'The lab tracker could not be loaded — nothing is shown.'
                : rows.length === 0 ? 'No enrollments in the tracker — no active lab violations.' : 'No rows match the current tile and filters.'}</td></tr>}
              {viewRows.map(r => (
                <tr key={r.therapy_id} style={{ cursor: 'pointer' }} onClick={() => setSel(r.therapy_id)}
                  onMouseEnter={e => (e.currentTarget.style.background = T.pb)} onMouseLeave={e => (e.currentTarget.style.background = '')}>
                  <td style={{ ...td, fontWeight: 700 }}>{r.rx_number || '—'}</td>
                  <td style={td}>{r.patient_name}<PatientLanguageBadge patientId={r.patient_id} /></td>
                  <td style={td}>{r.drug_name || '—'}</td>
                  <td style={td}>{r.open_count} open / {r.lab_count}</td>
                  <td style={td}><span style={stColor(r.status)}>{r.status.replace('_', ' ')}</span></td>
                  <td style={td} {...rowActions}>
                    <SearchableSelect value={r.lab_provider_id ?? ''} ariaLabel="Lab provider" placeholder="Select…"
                      onChange={(v: string) => w.quietly(w.setProvider(r.therapy_id, v || null))}
                      options={providers.map(p => ({ value: p.id, label: p.name }))} />
                  </td>
                  {/* UAT 2026-07-30: patient outreach status + DATE, provider outreach status + DATE,
                      and follow-up date are all user-editable inline (stopPropagation so the row
                      click-through to dial-in doesn't fire while editing). */}
                  <td style={td} {...rowActions}>
                    <div style={{ width: 130 }}><SearchableSelect value={r.patient_outreach_status ?? 'not_contacted'} ariaLabel="Patient outreach"
                      onChange={(v: string) => w.quietly(w.setDates(r.therapy_id, { patient_outreach_status: v }))}
                      options={[{ value: 'not_contacted', label: 'not contacted' }, { value: 'contacted', label: 'contacted' }, { value: 'unreachable', label: 'unreachable' }]} /></div>
                  </td>
                  {/* UAT 2026-08-11 (owner): the date MATCHES the status — contacted = the date the
                      patient was reached (auto-stamps today server-side), unreachable = the last
                      attempt (retry lives in Follow up), not contacted = no date to record. */}
                  <td style={td} {...rowActions}>
                    <DateInput label="Patient outreach date" value={r.patient_outreach_on}
                      disabled={(r.patient_outreach_status ?? 'not_contacted') === 'not_contacted'}
                      title={r.patient_outreach_status === 'contacted' ? 'Date the patient was reached'
                        : r.patient_outreach_status === 'unreachable' ? 'Last attempt date — set the retry date under Follow up'
                        : 'No contact yet — set the outreach status first'}
                      onChange={v => w.setDates(r.therapy_id, { patient_outreach_on: v ?? '' })} />
                  </td>
                  <td style={td} {...rowActions}>
                    <div style={{ width: 130 }}><SearchableSelect value={r.provider_outreach_status ?? 'not_contacted'} ariaLabel="Provider outreach"
                      onChange={(v: string) => w.quietly(w.setProviderOutreach(r.therapy_id, { status: v }))}
                      options={[{ value: 'not_contacted', label: 'not contacted' }, { value: 'contacted', label: 'contacted' }, { value: 'unreachable', label: 'unreachable' }]} /></div>
                  </td>
                  <td style={td} {...rowActions}>
                    <DateInput label="Provider outreach date" value={r.provider_outreach_on}
                      onChange={v => w.setProviderOutreach(r.therapy_id, { outreach_on: v ?? '' })} />
                  </td>
                  <td style={td}>{r.draw_types || '—'}</td>
                  <td style={td} {...rowActions}>
                    <LabNextActionCell therapyId={r.therapy_id} auto={r.next_action} text={r.next_action_text} by={r.next_action_by} onSaved={load} />
                  </td>
                  {/* ASP #28: the notes the next action is written from — clinical (enrollment) and internal (latest review note). */}
                  <td style={{ ...td, maxWidth: 220 }}>
                    <div><span style={{ fontSize: 10.5, color: T.mute }}>Clinical </span><TruncatedText text={r.clinical_notes} max={40} label="Clinical notes" /></div>
                    <div><span style={{ fontSize: 10.5, color: T.mute }}>Internal </span><TruncatedText text={r.last_review_note} max={40} label="Internal notes"
                      heading={r.last_review_note ? `${r.last_review_by || 'Reviewer'} · ${formatUsDateTime(r.last_review_at)}` : null} /></div>
                  </td>
                  <td style={td} {...rowActions}>
                    <DateInput label="Follow-up date" value={r.follow_up}
                      emphasis={r.patient_outreach_status === 'unreachable' && !r.follow_up ? 'overdue' : undefined}
                      title={r.patient_outreach_status === 'unreachable' ? 'Next attempt — when will the patient be reached again? (drives the Next action)' : 'Manual follow-up date'}
                      onChange={v => w.setDates(r.therapy_id, { follow_up_on: v ?? '' })} />
                  </td>
                </tr>))}
            </tbody>
          </table>
        </div>
        <div style={{ fontSize: 11, color: T.mute, marginTop: 6 }}>Showing {viewRows.length} of {total}{tile ? ` · ${LAB_TILES.find(t => t.key === tile)?.label}` : ''}. Click a header to sort; use the funnel to filter. Lab provider dropdown writes through to the enrollment.</div>
      </div>
    </div>
  )
}

function Detail({ therapyId, providers, onBack }: { therapyId: string; providers: api.LabProvider[]; onBack: () => void }) {
  const detail = useLabTrackerDetail(therapyId)
  const d = detail.data ?? null
  const w = useLabTrackerWrites()
  const [redrawFor, setRedrawFor] = useState<string | null>(null)
  const [redraw, setRedraw] = useState({ reason: '', redraw_on: '' })
  const { refetch } = detail
  const load = useCallback(() => { void refetch() }, [refetch])
  // A 403 (no break-glass) or 404 (another pharmacy's therapy) says so, with the way back.
  if (detail.isError) return (
    <div style={card}>
      <button style={btn} onClick={onBack}>← Back to tracker</button>
      <div role="alert" style={{ marginTop: 10, fontSize: 13, color: '#b91c1c' }}>{friendlyError(detail.error)}</div>
    </div>
  )
  if (!d) return <div style={card}>Loading…</div>

  const active = d.labs.filter(l => !l.locked)
  const allCollected = active.length > 0 && active.every(l => l.draw_status === 'collected')
  // A collected lab whose results date is older than 12 months is NOT current: it clears this
  // screen and then reads as a violation again at Clinical Review. Say so here rather than
  // promising a clearance that the dates do not support.
  // ISO day strings compare lexicographically, so this needs no Date parsing — and dodges the
  // "2026-08-01" - parsed - as - UTC - midnight trap that shifts a day backwards west of Greenwich.
  const cutoff = new Date(Date.now() - 365 * 864e5).toISOString().slice(0, 10)
  const stale = active.filter(l => l.draw_status === 'collected' && l.results_on && String(l.results_on) < cutoff)
  const setProvider = (v: string) => w.quietly(w.setProvider(therapyId, v || null))
  const editLab = (labId: string, body: { lab_company?: string; draw_type?: string; draw_status?: string; collected_on?: string; results_on?: string }) =>
    w.quietly(w.updateLab(labId, body))
  /// The locked-row note: why this order was re-drawn and when. LabPanel carries the generic row
  /// shape, so anything specific to THIS screen is looked up here rather than widened into it.
  const redrawNoteFor = (labId?: string) => {
    const l = d.labs.find(x => x.id === labId)
    return l?.redraw_reason ? `${l.redraw_reason} · re-draw ${usDate(l.redraw_on)}` : ''
  }
  const doRedraw = (labId: string) => {
    if (!redraw.reason.trim() || !redraw.redraw_on) { alert('Re-draw needs a reason AND a re-draw date.'); return }
    w.quietly(w.redraw(labId, redraw).then(() => { setRedrawFor(null); setRedraw({ reason: '', redraw_on: '' }) }))
  }

  return (
    <div>
      <div style={card}>
        <button style={btn} onClick={onBack}>← Back to tracker</button>
        <div style={{ display: 'flex', gap: 22, alignItems: 'end', flexWrap: 'wrap', marginTop: 10 }}>
          <div><div style={{ fontSize: 15, fontWeight: 700 }}>{d.drug_name || 'Therapy'}</div>
            <div style={{ fontSize: 12, color: T.soft }}>Rx {d.rx_number || '—'} · labs required for start</div></div>
          <label style={{ fontSize: 11, color: T.mute, display: 'flex', flexDirection: 'column', gap: 2 }}>Lab provider
            <SearchableSelect value={d.lab_provider_id ?? ''} ariaLabel="Lab provider" placeholder="Select…"
              onChange={setProvider} options={providers.map(p => ({ value: p.id, label: p.name }))} /></label>
          <div style={{ fontSize: 11, color: T.soft }}>Offers: {d.offered_draw_types.length ? d.offered_draw_types.map(t => <span key={t} style={{ ...pill(T.okbg, T.ok), marginRight: 4 }}>{t}</span>) : <i>select a provider</i>}</div>
          {/* D-CRX-62 wiring (owner review 2026-08-13): the lab-status artifact is generated from
              the screen where the labs are worked — it existed API-only. */}
          <button style={{ ...btn, marginLeft: 'auto' }} onClick={() => {
            const w = window.open('', '_blank')  // claimed in the gesture — popup-blocker safe
            api.generateCareRxStepDoc(therapyId, 'lab-status')
              .then(r => api.openCareRxPdfDocument(r.document_id, w))
              .catch(e => { w?.close(); alert(e instanceof Error ? e.message : 'Document failed') }) }}>Lab-status PDF</button>
        </div>
        {/* Owner review 2026-08-13: the dial-in is where staff "work outreach" — the same
            status-matched controls as the worklist row (contacted auto-stamps server-side). */}
        <div style={{ display: 'flex', gap: 14, alignItems: 'end', flexWrap: 'wrap', marginTop: 10, padding: '8px 10px', border: `0.5px solid ${T.border}`, borderRadius: 8, background: T.s2 }}>
          <label style={{ fontSize: 11, color: T.mute, display: 'flex', flexDirection: 'column', gap: 2 }}>Patient outreach
            <div style={{ width: 140 }}><SearchableSelect value={d.patient_outreach_status ?? 'not_contacted'} ariaLabel="Patient outreach"
              onChange={(v: string) => w.quietly(w.setDates(therapyId, { patient_outreach_status: v }))}
              options={[{ value: 'not_contacted', label: 'not contacted' }, { value: 'contacted', label: 'contacted' }, { value: 'unreachable', label: 'unreachable' }]} /></div></label>
          <label style={{ fontSize: 11, color: T.mute, display: 'flex', flexDirection: 'column', gap: 2 }}>
            {d.patient_outreach_status === 'contacted' ? 'Date reached' : d.patient_outreach_status === 'unreachable' ? 'Last attempt' : 'Contact date'}
            {/* review 2026-10-02: these were raw date inputs that saved on EVERY keystroke (0002, 0020…). */}
            <DateInput value={d.patient_outreach_on ? String(d.patient_outreach_on) : null} label="Patient contact date"
              disabled={(d.patient_outreach_status ?? 'not_contacted') === 'not_contacted'}
              onChange={v => w.setDates(therapyId, { patient_outreach_on: v ?? '' })} /></label>
          <label style={{ fontSize: 11, color: T.mute, display: 'flex', flexDirection: 'column', gap: 2 }}>
            {d.patient_outreach_status === 'unreachable' ? 'Retry patient on' : 'Follow up'}
            <DateInput value={d.follow_up ? String(d.follow_up) : null} label="Patient follow-up date"
              emphasis={d.patient_outreach_status === 'unreachable' && !d.follow_up ? 'overdue' : undefined}
              onChange={v => w.setDates(therapyId, { follow_up_on: v ?? '' })} /></label>
          {d.patient_outreach_status === 'unreachable' && !d.follow_up &&
            <span style={{ fontSize: 11, color: '#b45309' }}>Set the retry date — it drives the Next action.</span>}
        </div>

        {allCollected && stale.length === 0 && <div style={{ ...pill(T.okbg, T.ok), display: 'block', padding: '8px 12px', fontSize: 12.5, marginTop: 12, fontWeight: 600 }}>✓ All required labs collected — this enrollment moves to Clinical Review and leaves the tracker on refresh.</div>}
        {allCollected && stale.length > 0 && <div style={{ ...pill(T.warnbg, T.warn), display: 'block', padding: '8px 12px', fontSize: 12.5, marginTop: 12, fontWeight: 600 }}>
          ⚠ Collected, but {stale.map(l => l.analyte.replace(/_/g, ' ').toUpperCase()).join(', ')} {stale.length === 1 ? 'has' : 'have'} results older than 12 months — {stale.length === 1 ? 'it' : 'they'} will read as a violation again at Clinical Review. Re-draw, or correct the results date.
        </div>}

        {/* D-CRX-80: the shared LabPanel. This screen's own bulk bar and table were the ONLY
            place in CareRx an operator could edit labs in bulk, and its analyte labelling
            (analyte.replace(/_/g,' ').toUpperCase()) was one of four in the module — none of which
            agreed with the token the drug record actually stores. Both now come from the one
            component and the one vocabulary. Re-draw stays here as a rowAction: it is this
            screen's own workflow, not something every lab surface has. */}
        <div style={{ marginTop: 12 }}>
          <LabPanel
            rows={d.labs.map(l => ({
              id: l.id,
              analyte: l.analyte,
              lab_company: l.lab_company,
              draw_type: l.draw_type,
              draw_status: l.draw_status,
              collected_on: dateStr(l.collected_on),
              results_on: dateStr(l.results_on),
              locked: l.locked,
              note: l.is_redraw_order ? '(re-draw order)' : null,
            }))}
            editable={{ labCompany: true, drawType: true, drawStatus: true, collectedOn: true, resultsOn: true }}
            drawStatusOptions={DRAW_STATUS}
            drawTypeOptions={d.offered_draw_types}
            onEdit={(row, patch) => {
              // 'redraw_needed' opens this screen's re-draw dialog rather than writing straight
              // through — locking a lab and opening a replacement order needs a reason and a date.
              if (patch.draw_status === 'redraw_needed') { setRedrawFor(row.id!); return }
              editLab(row.id!, patch as Parameters<typeof editLab>[1])
            }}
            onBulk={(rows, patch) => {
              w.quietly(w.bulk({
                lab_ids: rows.map(r => r.id!).filter(Boolean),
                lab_company: patch.lab_company || undefined,
                draw_type: patch.draw_type || undefined,
                draw_status: patch.draw_status || undefined,
                collected_on: patch.collected_on || undefined,
                results_on: patch.results_on || undefined,
              }))
            }}
            rowActions={l => l.locked
              ? <span style={{ fontSize: 11, color: T.mute }}>
                  {redrawNoteFor(l.id)}
                </span>
              : redrawFor === l.id ? (
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                  <input style={{ ...inp, width: 160 }} placeholder="reason (required)" value={redraw.reason}
                    onChange={e => setRedraw({ ...redraw, reason: e.target.value })} />
                  <DateInput value={redraw.redraw_on || null} label="Re-draw date"
                    onChange={v => setRedraw(r => ({ ...r, redraw_on: v ?? '' }))} />
                  <button style={btnP} onClick={() => doRedraw(l.id!)}>Lock &amp; open new order</button>
                  <button style={btn} onClick={() => { setRedrawFor(null); load() }}>Cancel</button>
                </div>) : null}
            emptyText="No baseline labs required for this therapy."
          />
        </div>
      </div>
    </div>
  )
}
